<template>
  <div class="canvas-shell">
    <slot v-if="showFallback">
      <img
        :src="props.bottleImage"
        alt=""
        class="canvas-shell__fallback"
        aria-hidden="true"
        loading="eager"
        decoding="async"
        fetchpriority="high"
        :style="{ viewTransitionName: `product-photo-${props.id}` }"
      />
    </slot>
    <canvas ref="canvas" class="canvas-shell__canvas" />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  labelTexture: {
    type: String,
    required: true,
  },
  capTexture: {
    type: String,
    required: true,
  },
  bottleImage: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['loaded'])

const MODEL_PATH = '/models/bottle2.glb'
const ENVIRONMENT_PATH = '/images/environment.webp'
const VIEW_HEIGHT = 10
const BOTTLE_SCALE = 0.235
const MIN_BOTTLE_SCALE_FACTOR = 0.4
const MAX_BOTTLE_SCALE_FACTOR = 1
const MIN_SCALE_WIDTH = 360
const MAX_SCALE_WIDTH = 710
const BOTTLE_POSITION = { x: 0, y: 0, z: 0.007 }
const BOTTLE_Y_ROTATION_START = 0.5
const BOTTLE_Y_ROTATION_END = 13.19
const BOTTLE_SCROLL_EDGE_BUFFER = 0.62
const TONE_MAPPING_EXPOSURE = 0.54
const BOTTLE_ENV_MAP_INTENSITY = 0.07
const LABEL_ENV_MAP_INTENSITY = 0.01
const LABEL_TEXTURE_CONTRAST = 1.1
const ATTENUATION_COLOR = '#d9f2d9'
const ATTENUATION_DISTANCE = 0.18
const AMBIENT_LIGHT_INTENSITY = 2.16
const LABEL_FILL_LIGHT_INTENSITY = 7.09
const ENV_ROTATION = { x: 245, y: 225, z: 133 }
const FRONT_LIGHT_INTENSITY = 9.77
const FRONT_LIGHT_POSITION = { x: -50, y: -18.4, z: 50 }
const FRONT_LIGHT_ROTATION = { x: 197, y: 156, z: 159 }
const FRONT_LIGHT_DISTANCE = 172.8

const canvas = ref(null)
const showFallback = ref(true)

let THREE
let GLTFLoader
let DRACOLoader
let textureLoader
let gsap
let ScrollTrigger
let dracoLoader
let renderer
let scene
let camera
let bottleGroup
let bottleBounds = null
let frontLight
let frontLightRig
let resizeObserver
let rebuildVersion = 0
let scrollTween = null
let deferredBuildFrame = 0
let deferredBuildIdleCallback = null
let deferredBuildTimer = null
let isMounted = false

const environmentMaterials = []
const bottleScrollState = { progress: 0 }

function getBottleScale() {
  if (!canvas.value || !THREE) {
    return BOTTLE_SCALE
  }

  const width = window.innerWidth
  const progress = THREE.MathUtils.clamp(
    (width - MIN_SCALE_WIDTH) / (MAX_SCALE_WIDTH - MIN_SCALE_WIDTH),
    0,
    1
  )
  const scaleFactor = THREE.MathUtils.lerp(
    MIN_BOTTLE_SCALE_FACTOR,
    MAX_BOTTLE_SCALE_FACTOR,
    progress
  )

  return BOTTLE_SCALE * scaleFactor
}

function syncBottleScale() {
  if (!bottleGroup) {
    return
  }

  bottleGroup.scale.setScalar(getBottleScale())
}

function getBottleScrollBounds() {
  if (!canvas.value || !camera || !bottleGroup || !bottleBounds) {
    return { startY: BOTTLE_POSITION.y, endY: BOTTLE_POSITION.y }
  }

  const scaledTopOffset = bottleBounds.top * bottleGroup.scale.y
  const scaledBottomOffset = bottleBounds.bottom * bottleGroup.scale.y

  return {
    startY: camera.top - scaledTopOffset - BOTTLE_SCROLL_EDGE_BUFFER,
    endY: camera.bottom + scaledBottomOffset + BOTTLE_SCROLL_EDGE_BUFFER,
  }
}

async function ensureThree() {
  if (
    THREE &&
    GLTFLoader &&
    DRACOLoader &&
    textureLoader &&
    dracoLoader
  ) {
    return
  }

  const [
    threeModule,
    gltfLoaderModule,
    dracoLoaderModule,
  ] = await Promise.all([
    import('three'),
    import('three/examples/jsm/loaders/GLTFLoader.js'),
    import('three/examples/jsm/loaders/DRACOLoader.js'),
  ])

  THREE = threeModule
  GLTFLoader = gltfLoaderModule.GLTFLoader
  DRACOLoader = dracoLoaderModule.DRACOLoader
  textureLoader = new THREE.TextureLoader()

  dracoLoader = new DRACOLoader()
  dracoLoader.setDecoderPath('/draco/')
}

async function ensureGsap() {
  if (gsap && ScrollTrigger) {
    return
  }

  const [gsapModule, scrollTriggerModule] = await Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger'),
  ])

  gsap = gsapModule.default
  ScrollTrigger = scrollTriggerModule.ScrollTrigger
  gsap.registerPlugin(ScrollTrigger)
}

function cleanupScrollAnimation() {
  scrollTween?.scrollTrigger?.kill()
  scrollTween?.kill()
  scrollTween = null
}

function teardownScene() {
  if (scene) {
    scene.traverse((object) => {
      if (object.geometry) {
        object.geometry.dispose()
      }

      if (object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material]

        materials.forEach((material) => {
          Object.values(material).forEach((value) => {
            if (value && typeof value === 'object' && 'isTexture' in value && value.isTexture) {
              value.dispose()
            }
          })

          material.dispose()
        })
      }
    })
  }

  renderer?.dispose()

  scene = null
  camera = null
  renderer = null
  bottleGroup = null
  bottleBounds = null
  frontLight = null
  frontLightRig = null
  environmentMaterials.length = 0
  cleanupScrollAnimation()
}

function renderScene() {
  if (renderer && scene && camera) {
    renderer.render(scene, camera)
  }
}

function applyLabelTextureContrast(material) {
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#include <map_fragment>
      diffuseColor.rgb = ((diffuseColor.rgb - 0.5) * ${LABEL_TEXTURE_CONTRAST.toFixed(2)}) + 0.5;
      diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);`
    )
  }

  material.customProgramCacheKey = () => `label-contrast-${LABEL_TEXTURE_CONTRAST}`
  material.needsUpdate = true
}

function syncLightTransform() {
  if (!frontLight || !frontLightRig) {
    return
  }

  frontLightRig.position.set(
    FRONT_LIGHT_POSITION.x,
    FRONT_LIGHT_POSITION.y,
    FRONT_LIGHT_POSITION.z
  )

  frontLightRig.rotation.set(
    THREE.MathUtils.degToRad(FRONT_LIGHT_ROTATION.x),
    THREE.MathUtils.degToRad(FRONT_LIGHT_ROTATION.y),
    THREE.MathUtils.degToRad(FRONT_LIGHT_ROTATION.z)
  )

  frontLight.position.set(0, 0, FRONT_LIGHT_DISTANCE)
  renderScene()
}

function syncEnvironmentRotation() {
  if (!scene) {
    return
  }

  const rotation = new THREE.Euler(
    THREE.MathUtils.degToRad(ENV_ROTATION.x),
    THREE.MathUtils.degToRad(ENV_ROTATION.y),
    THREE.MathUtils.degToRad(ENV_ROTATION.z)
  )

  scene.environmentRotation = rotation
  scene.backgroundRotation = rotation

  environmentMaterials.forEach((material) => {
    if ('envMapRotation' in material && material.envMapRotation) {
      material.envMapRotation.copy(rotation)
      material.needsUpdate = true
    }
  })

  renderScene()
}

function applyBottleScrollPose() {
  if (!bottleGroup || !THREE) {
    return
  }

  const positionProgress = THREE.MathUtils.clamp(bottleScrollState.progress, 0, 1)
  const spinProgress = positionProgress * 1.8
  const wobble = Math.sin(spinProgress * Math.PI) * .08
  const scrollBounds = getBottleScrollBounds()

  bottleGroup.rotation.set(
    THREE.MathUtils.lerp(-0.2, 0.18, positionProgress) + wobble,
    THREE.MathUtils.lerp(BOTTLE_Y_ROTATION_START, BOTTLE_Y_ROTATION_END, positionProgress),
    THREE.MathUtils.lerp(0.1, 0.12, positionProgress)
  )

  bottleGroup.position.set(
    BOTTLE_POSITION.x,
    THREE.MathUtils.lerp(
      scrollBounds.startY,
      scrollBounds.endY,
      positionProgress
    ),
    BOTTLE_POSITION.z
  )

  renderScene()
}

async function setupScrollAnimation() {
  await ensureGsap()

  const triggerElement = canvas.value?.closest('.product-holder')

  if (!triggerElement) {
    return
  }

  cleanupScrollAnimation()
  bottleScrollState.progress = 0
  applyBottleScrollPose()

  scrollTween = ScrollTrigger.create({
    trigger: triggerElement,
    start: 'top top',
    end: '+=1000',
    invalidateOnRefresh: true,
    fastScrollEnd: true,
    onUpdate: (self) => {
      bottleScrollState.progress = self.progress
      applyBottleScrollPose()
    },
  })
}

async function loadTexture(url, options = {}) {
  const texture = await textureLoader.loadAsync(url)

  Object.assign(texture, {
    wrapS: options.wrapS ?? THREE.ClampToEdgeWrapping,
    wrapT: options.wrapT ?? THREE.ClampToEdgeWrapping,
    magFilter: options.magFilter ?? THREE.LinearFilter,
    minFilter: options.minFilter ?? THREE.LinearMipmapLinearFilter,
    anisotropy: options.anisotropy ?? 8,
  })

  texture.generateMipmaps = true

  if (options.colorSpace) {
    texture.colorSpace = options.colorSpace
  }

  if (options.mapping) {
    texture.mapping = options.mapping
  }

  texture.needsUpdate = true
  return texture
}

async function loadEnvironmentMap(pmremGenerator) {
  await ensureThree()

  const envTexture = await textureLoader.loadAsync(ENVIRONMENT_PATH)
  envTexture.mapping = THREE.EquirectangularReflectionMapping
  envTexture.colorSpace = THREE.SRGBColorSpace

  const envRenderTarget = pmremGenerator.fromEquirectangular(envTexture)
  envTexture.dispose()

  return envRenderTarget.texture
}

async function loadBottleModel() {
  const gltfLoader = new GLTFLoader()
  gltfLoader.setDRACOLoader(dracoLoader)

  return gltfLoader.loadAsync(MODEL_PATH)
}

async function buildScene() {
  if (!canvas.value) {
    return
  }

  await ensureThree()

  showFallback.value = true

  const currentBuild = ++rebuildVersion

  const colorPath =
    'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/bottles/model/bottle-texture.webp'
  const labelPath = props.labelTexture
  const capPath = props.capTexture

  scene = new THREE.Scene()
  environmentMaterials.length = 0

  const { clientWidth, clientHeight } = canvas.value
  const aspect = clientWidth / clientHeight

  camera = new THREE.OrthographicCamera(
    (-VIEW_HEIGHT * aspect) / 2,
    (VIEW_HEIGHT * aspect) / 2,
    VIEW_HEIGHT / 2,
    -VIEW_HEIGHT / 2,
    0.1,
    100
  )

  camera.position.set(0, 0, 10)

  renderer = new THREE.WebGLRenderer({
    canvas: canvas.value,
    antialias: true,
    alpha: true,
    premultipliedAlpha: true,
    powerPreference: 'high-performance',
  })

  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE
  renderer.localClippingEnabled = true
  renderer.setClearColor(0x000000, 0)
  renderer.setSize(clientWidth, clientHeight, false)
  renderer.setPixelRatio(Math.max(window.devicePixelRatio, 2))

  const pmremGenerator = new THREE.PMREMGenerator(renderer)
  pmremGenerator.compileEquirectangularShader()

  const ambientLight = new THREE.AmbientLight('#ffffff', AMBIENT_LIGHT_INTENSITY)

  const pointLight = new THREE.PointLight(0xfff5ea, 0.06, 36)
  pointLight.position.set(0.25, 2.1, 8.5)

  const labelFillLight = new THREE.PointLight('#ffffff', LABEL_FILL_LIGHT_INTENSITY, 32, 2)
  labelFillLight.position.set(-4.5, 1.2, 6.2)

  frontLightRig = new THREE.Group()
  frontLight = new THREE.DirectionalLight('#ffffff', FRONT_LIGHT_INTENSITY)
  frontLightRig.add(frontLight)

  const hemiLight = new THREE.HemisphereLight(0xfafafa, 0x081406, 0.08)

  scene.add(ambientLight, hemiLight, pointLight, labelFillLight, frontLightRig, camera)

  syncLightTransform()

  let envMap
  let colorMap
  let labelMap
  let capMap
  let gltf

  ;[envMap, colorMap, labelMap, capMap, gltf] = await Promise.all([
    loadEnvironmentMap(pmremGenerator),
    loadTexture(colorPath, {
      colorSpace: THREE.SRGBColorSpace,
    }),
    loadTexture(labelPath, {
      colorSpace: THREE.SRGBColorSpace,
    }),
    loadTexture(capPath, {
      colorSpace: THREE.SRGBColorSpace,
    }),
    loadBottleModel(),
  ])

  if (currentBuild !== rebuildVersion) {
    envMap.dispose()
    colorMap.dispose()
    labelMap.dispose()
    capMap.dispose()
    pmremGenerator.dispose()
    return
  }

  scene.environment = envMap
  pmremGenerator.dispose()

  const materialBase = {
    map: colorMap,
    metalness: 0,
    envMap,
  }

  const frontMaterial = new THREE.MeshStandardMaterial({
    ...materialBase,
    roughness: 0.25,
    transparent: true,
    opacity: 0.8,
    depthWrite: true,
    depthTest: true,
    side: THREE.FrontSide,
    envMapIntensity: BOTTLE_ENV_MAP_INTENSITY,
    color: new THREE.Color(ATTENUATION_COLOR),
  })

  const labelMaterial = frontMaterial.clone()
  labelMaterial.map = labelMap
  labelMaterial.roughness = 1
  labelMaterial.metalness = .4
  labelMaterial.envMapIntensity = LABEL_ENV_MAP_INTENSITY
  labelMaterial.transparent = true
  labelMaterial.opacity = 1
  labelMaterial.alphaTest = 0.9
  labelMaterial.depthWrite = false
  labelMaterial.polygonOffset = true
  labelMaterial.polygonOffsetFactor = -1
  labelMaterial.polygonOffsetUnits = -1
  applyLabelTextureContrast(labelMaterial)

  const capMaterial = frontMaterial.clone()
  capMaterial.map = capMap
  capMaterial.roughness = 0.25
  capMaterial.metalness = .2
  capMaterial.transparent = true
  capMaterial.opacity = 1
  capMaterial.alphaTest = 0.1
  capMaterial.depthWrite = false
  capMaterial.polygonOffset = true
  capMaterial.polygonOffsetFactor = -2
  capMaterial.polygonOffsetUnits = -2

  bottleGroup = new THREE.Group()

  gltf.scene.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return
    }

    const baseGeometry = child.geometry.clone()

    const localBox = new THREE.Box3().setFromBufferAttribute(
      baseGeometry.getAttribute('position')
    )

    const bodyMaterial = frontMaterial.clone()
    const tunedCapMaterial = capMaterial.clone()

    environmentMaterials.push(bodyMaterial, labelMaterial, tunedCapMaterial)

    const frontMesh = new THREE.Mesh(baseGeometry, bodyMaterial)
    const labelMesh = new THREE.Mesh(baseGeometry, labelMaterial)
    const capMesh = new THREE.Mesh(baseGeometry, tunedCapMaterial)

    frontMesh.renderOrder = 0
    labelMesh.renderOrder = 1
    capMesh.renderOrder = 2

    bottleGroup.add(frontMesh, labelMesh, capMesh)
  })

  const bottleBox = new THREE.Box3().setFromObject(bottleGroup)
  bottleBounds = {
    top: bottleBox.max.y,
    bottom: -bottleBox.min.y,
  }

  syncBottleScale()
  bottleGroup.position.set(BOTTLE_POSITION.x, BOTTLE_POSITION.y, BOTTLE_POSITION.z)

  scene.add(bottleGroup)

  syncEnvironmentRotation()
  await setupScrollAnimation()

  showFallback.value = false
  emit('loaded')
}

function handleResize() {
  if (!renderer || !camera || !canvas.value) {
    return
  }

  const { clientWidth, clientHeight } = canvas.value

  renderer.setSize(clientWidth, clientHeight, false)

  const aspect = clientWidth / clientHeight

  camera.left = (-VIEW_HEIGHT * aspect) / 2
  camera.right = (VIEW_HEIGHT * aspect) / 2
  camera.top = VIEW_HEIGHT / 2
  camera.bottom = -VIEW_HEIGHT / 2
  camera.updateProjectionMatrix()
  syncBottleScale()
  applyBottleScrollPose()
  ScrollTrigger?.refresh()
}

function observeCanvasResize() {
  if (resizeObserver || !canvas.value) {
    return
  }

  resizeObserver = new ResizeObserver(handleResize)
  resizeObserver.observe(canvas.value)
}

function clearDeferredBuild() {
  if (deferredBuildFrame) {
    window.cancelAnimationFrame(deferredBuildFrame)
    deferredBuildFrame = 0
  }

  if (deferredBuildIdleCallback && window.cancelIdleCallback) {
    window.cancelIdleCallback(deferredBuildIdleCallback)
    deferredBuildIdleCallback = null
  }

  if (deferredBuildTimer) {
    window.clearTimeout(deferredBuildTimer)
    deferredBuildTimer = null
  }
}

async function startDeferredBuild() {
  clearDeferredBuild()

  if (!isMounted || !canvas.value) {
    return
  }

  await buildScene()

  if (!isMounted || !canvas.value) {
    return
  }

  observeCanvasResize()
}

function scheduleDeferredBuild() {
  clearDeferredBuild()

  deferredBuildFrame = window.requestAnimationFrame(() => {
    deferredBuildFrame = window.requestAnimationFrame(() => {
      deferredBuildFrame = 0

      if (window.requestIdleCallback) {
        deferredBuildIdleCallback = window.requestIdleCallback(() => {
          deferredBuildIdleCallback = null
          startDeferredBuild()
        }, { timeout: 900 })
        return
      }

      deferredBuildTimer = window.setTimeout(() => {
        deferredBuildTimer = null
        startDeferredBuild()
      }, 250)
    })
  })
}

onMounted(() => {
  isMounted = true
  scheduleDeferredBuild()
})

watch(
  () => [props.labelTexture, props.capTexture],
  () => {
    teardownScene()
    scheduleDeferredBuild()
  }
)

onBeforeUnmount(() => {
  isMounted = false
  clearDeferredBuild()

  if (resizeObserver && canvas.value) {
    resizeObserver.unobserve(canvas.value)
  }

  resizeObserver?.disconnect()
  teardownScene()

  dracoLoader?.dispose()
  dracoLoader = null
})
</script>
